import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const EmailContactModal = ({ isOpen, onClose, contact }) => {
  const [emailData, setEmailData] = useState({
    subject: '',
    body: '',
    cc: '',
    bcc: ''
  });
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleInputChange = (field, value) => {
    setEmailData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSend = async () => {
    if (!emailData?.subject || !emailData?.body) {
      setError('Subject and message are required');
      return;
    }

    if (!contact?.email) {
      setError('Contact does not have an email address');
      return;
    }

    try {
      setSending(true);
      setError('');

      // Call Supabase Edge Function for sending email
      const { data, error: functionError } = await supabase?.functions?.invoke('send-email', {
        body: {
          recipientEmail: contact?.email,
          subject: emailData?.subject,
          bodyHtml: `<div style="font-family: Arial, sans-serif; line-height: 1.6;">${emailData?.body?.replace(/\n/g, '<br>')}</div>`,
          bodyText: emailData?.body
        }
      });

      if (functionError) throw functionError;

      setSuccess('Email sent successfully!');
      setTimeout(() => {
        setEmailData({ subject: '', body: '', cc: '', bcc: '' });
        setSuccess('');
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error sending email:', err);
      setError(err?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setEmailData({ subject: '', body: '', cc: '', bcc: '' });
    setError('');
    setSuccess('');
    setShowCc(false);
    setShowBcc(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-background border border-border rounded-xl shadow-elevation-2 w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="Mail" size={20} className="text-primary" />
            <div>
              <h2 className="text-lg font-semibold text-foreground">Send Email</h2>
              <p className="text-sm text-muted-foreground">
                To: {contact?.fullName || contact?.firstName} ({contact?.email})
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose}>
            <Icon name="X" size={16} />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
            </div>
          )}
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center space-x-2">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm">{success}</span>
            </div>
          )}

          {/* Recipient (Read-only) */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">To</label>
            <div className="px-3 py-2 bg-muted/30 border border-border rounded-lg text-sm text-foreground">
              {contact?.email}
            </div>
          </div>

          {/* CC/BCC Toggle */}
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowCc(!showCc)}
              className="text-muted-foreground"
            >
              Cc
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowBcc(!showBcc)}
              className="text-muted-foreground"
            >
              Bcc
            </Button>
          </div>

          {/* CC Field */}
          {showCc && (
            <Input
              label="Cc"
              type="email"
              placeholder="Carbon copy recipients"
              value={emailData?.cc}
              onChange={(e) => handleInputChange('cc', e?.target?.value)}
            />
          )}

          {/* BCC Field */}
          {showBcc && (
            <Input
              label="Bcc"
              type="email"
              placeholder="Blind carbon copy recipients"
              value={emailData?.bcc}
              onChange={(e) => handleInputChange('bcc', e?.target?.value)}
            />
          )}

          {/* Subject */}
          <Input
            label="Subject"
            type="text"
            placeholder="Email subject"
            value={emailData?.subject}
            onChange={(e) => handleInputChange('subject', e?.target?.value)}
            required
          />

          {/* Body */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Message</label>
            <div className="border border-border rounded-lg">
              {/* Toolbar */}
              <div className="flex items-center space-x-1 p-2 border-b border-border bg-muted/30">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Icon name="Bold" size={14} />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Icon name="Italic" size={14} />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Icon name="Underline" size={14} />
                </Button>
                <div className="w-px h-4 bg-border mx-1" />
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Icon name="List" size={14} />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Icon name="Link" size={14} />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <Icon name="Paperclip" size={14} />
                </Button>
              </div>
              
              {/* Text Area */}
              <textarea
                className="w-full h-48 p-3 resize-none border-0 focus:outline-none focus:ring-0 bg-transparent"
                placeholder="Type your message here..."
                value={emailData?.body}
                onChange={(e) => handleInputChange('body', e?.target?.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-border">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Info" size={14} />
            <span>Email will be sent from your configured sender address</span>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" onClick={handleClose} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={handleSend} loading={sending} disabled={sending}>
              <Icon name="Send" size={16} className="mr-2" />
              Send Email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailContactModal;